import { createContext, useContext, useEffect, useState } from "react";
import ScrollCue from "./ScrollCue";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { api, L } from "./lib";
import "./louise.css";

const SiteCtx = createContext({ site: null, reload: () => {} });
export const useSite = () => useContext(SiteCtx);

const NAV = [
  ["/art", "Her Art", "nav-art"],
  ["/remembering", "Remembering Louise", "nav-remembering"],
  ["/photographs", "Photographs", "nav-photographs"],
  ["/life", "Her Life", "nav-life"],
  ["/in-motion", "Louise in Motion", "nav-motion"],
  ["/arrangements", "Arrangements", "nav-arrangements"],
];

function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  const items = NAV;
  const link = ({ isActive }) =>
    `text-[0.92rem] transition-colors duration-200 ${isActive ? "text-lou-clay" : "text-lou-ink hover:text-lou-clay"}`;
  return (
    <header className="sticky top-0 z-40 bg-lou-ivory/85 backdrop-blur-md lou-stripe">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between gap-6">
        <Link to={L("/")} data-testid="nav-home" className="font-lou text-xl sm:text-2xl tracking-tight">Louise Gatanas</Link>
        <nav aria-label="Main" className="hidden xl:flex items-center gap-7">
          {items.map(([p, label, id]) => <NavLink key={p} to={L(p)} className={link} data-testid={id}>{label}</NavLink>)}
          <Link to={L("/share")} className="lou-btn !py-2 !px-5 text-sm" data-testid="nav-share">Share a memory</Link>
        </nav>
        <button className="xl:hidden p-2" aria-expanded={open} aria-controls="mobile-nav" aria-label="Menu"
          onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <nav id="mobile-nav" aria-label="Main" className="xl:hidden border-t border-lou-line bg-lou-ivory px-6 py-6 flex flex-col gap-4">
          {items.map(([p, label, id]) => <NavLink key={p} to={L(p)} className={link} data-testid={`m-${id}`}>{label}</NavLink>)}
          <Link to={L("/share")} className="lou-btn self-start" data-testid="m-nav-share">Share a memory</Link>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-32 lou-stripe-top bg-lou-sand/70">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid gap-10 md:grid-cols-3">
        <div>
          <p className="font-lou text-2xl">Louise Gatanas</p>
          <p className="text-sm text-lou-soft mt-3 max-w-xs leading-relaxed">A family archive of her life, her art and the people who loved her.</p>
        </div>
        <div className="text-sm space-y-2">
          <Link to={L("/share")} className="block hover:text-lou-clay" data-testid="footer-share">Share a memory or photograph</Link>
          <Link to={L("/correction")} className="block hover:text-lou-clay" data-testid="footer-correction">Request a correction or removal</Link>
        </div>
        <p className="text-xs text-lou-soft md:text-right leading-relaxed">
          Kept by her family. Contributions are attributed to the people who share them; email addresses are never published.
          <br /><span className="opacity-70">A family legacy inspired by <a href="https://eversoul.world" target="_blank" rel="noreferrer" className="underline hover:text-lou-clay" data-testid="footer-built-by">EverSoul</a>.</span>
        </p>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const [site, setSite] = useState(null);
  const reload = () => api.get("/home").then((r) => setSite(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  return (
    <SiteCtx.Provider value={{ site, reload }}>
      <div className="lou-root lou-grain relative">
        <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 bg-lou-ink text-white px-4 py-2">Skip to content</a>
        {site?.show_samples && (
          <div data-testid="sample-banner" className="bg-lou-ink text-lou-paper text-xs text-center px-4 py-2 tracking-wide">
            Development preview: anything marked <strong>SAMPLE</strong> is a placeholder, not authentic material about Louise.
          </div>
        )}
        <Header />
        <main id="main" className="relative z-[2]">{children}</main>
        <ScrollCue />
        <Footer />
      </div>
    </SiteCtx.Provider>
  );
}
